/**
 * Hook for leaving a conversation with confirmation.
 */

import { useLeaveConversationMutation } from '@saaga/shared/store/api.store'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useDialogState } from '@/hooks/use-dialog-state.hook'
import { useOrgSlug } from '@/hooks/use-org-slug.hook'

interface UseLeaveConversationParams {
  conversationId: string | null
}

export interface UseLeaveConversationReturn {
  isDialogOpen: boolean
  isLeavingConversation: boolean
  openDialog: () => void
  closeDialog: () => void
  setDialogOpen: (isOpen: boolean) => void
  handleLeaveConversation: () => Promise<void>
}

export const useLeaveConversation = ({
  conversationId,
}: UseLeaveConversationParams): UseLeaveConversationReturn => {
  const navigate = useNavigate()
  const orgSlug = useOrgSlug()
  const dialog = useDialogState()
  const [leaveConversation, { isLoading: isLeavingConversation }] = useLeaveConversationMutation()

  const handleLeaveConversation = useCallback(async () => {
    if (!conversationId) {
      toast.error('Cannot leave: No active conversation')
      return
    }

    try {
      await leaveConversation(conversationId).unwrap()
      trackEvent('participant_left', {})
      toast.success('You have left the conversation')
      dialog.close()

      const basePath = orgSlug ? `/${orgSlug}` : '/'
      navigate(basePath, { replace: true })
    } catch (error) {
      logger.error('Failed to leave conversation:', error)
      toast.error('Failed to leave conversation. Please try again.')
    }
  }, [conversationId, leaveConversation, dialog, navigate, orgSlug])

  return {
    isDialogOpen: dialog.isOpen,
    isLeavingConversation,
    openDialog: dialog.open,
    closeDialog: dialog.close,
    setDialogOpen: dialog.setIsOpen,
    handleLeaveConversation,
  }
}
